<template>
  <div>
    <span
      :class="[mode === 'dark' ? 'text-dark-15' : 'text-gray-600']"
      class="cursor-pointer"
      @click="showSearch"
    >
      <Search :size="22" :stroke-width="1.75"
    /></span>
    <div
      v-if="isSearch"
      class="bg-opacity-40 fixed inset-0 z-[9999] flex items-center justify-center p-4"
      :class="mode === 'dark' ? 'bg-white-100' : 'bg-black-100'"
    >
      <div
        class="rounded-xl w-full mx-auto"
        :class="[
          mode === 'dark' ? 'bg-dark-70' : 'bg-white-100',
          searchQuery ? 'max-w-7xl' : 'max-w-3xl',
        ]"
      >
        <div
          class="flex items-center justify-between p-4 border-b"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <h5 class="font-medium text-[17px] leading-none">Search</h5>
          <X
            :size="22"
            :stroke-width="1.75"
            @click="closeSearch"
            class="cursor-pointer"
          />
        </div>
        <div
          class="without-scrollbar max-h-modal overflow-y-auto p-4 rounded-bl-xl rounded-br-xl"
          :class="mode === 'dark' ? 'bg-dark-70' : 'bg-gray-20 bg-opacity-30'"
        >
          <div>
            <div class="flex items-center relative">
              <span
                class="absolute bottom-0 left-2.5 flex h-11 items-center text-gray-0 text-opacity-70"
              >
                <Search :size="20" :stroke-width="1.75" />
              </span>
              <input
                id="search"
                type="search"
                class="form-field pl-9"
                placeholder="Search for keyword discovery & keyword spy"
                @input="getSearchResults"
                v-model="searchQuery"
              />
            </div>
            <div v-if="searchQuery" class="mt-4">
              <div class="grid grid-cols-3 gap-4">
                <div
                  class="border rounded-md overflow-hidden"
                  :class="
                    mode === 'dark'
                      ? 'border-dark-50 bg-dark-70'
                      : 'bg-white-100'
                  "
                >
                  <div
                    class="p-3 border-b"
                    :class="[mode === 'dark' ? 'border-dark-50' : '']"
                  >
                    <h3 class="leading-none mb-2 font-semibold tex-[17px]">
                      Keyword discovery
                    </h3>
                    <p
                      class="text-[15px] leading-none"
                      :class="
                        mode === 'dark' ? 'text-dark-15' : 'text-gray-600'
                      "
                    >
                      Total {{ discoveryKeywordSearchResults.count }}
                      {{
                        discoveryKeywordSearchResults.count > 1
                          ? "results"
                          : "result"
                      }}
                      found
                    </p>
                  </div>
                  <div v-if="discoveryKeywordSearchResults.count > 0">
                    <div
                      class="p-3 h-[310px] overflow-y-auto without-scrollbar"
                    >
                      <ul class="grid gap-1 pl-4 list-disc">
                        <li
                          :class="[
                            mode === 'dark' ? 'text-dark-15' : 'text-gray-600',
                          ]"
                          v-for="(
                            result, key
                          ) in discoveryKeywordSearchResults.results"
                          :key="key"
                        >
                          <div
                            @click="
                              redirectTo(result.identifier, 'keywordplanner')
                            "
                            class="text-[15px] hover:text-primary-100 cursor-pointer"
                            :class="[
                              mode === 'dark'
                                ? 'text-dark-15'
                                : 'text-gray-600',
                            ]"
                          >
                            {{ result.content }}
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div
                    v-if="
                      discoveryKeywordSearchResults.count === 0 &&
                      !isDiscoveryKeywordLoading
                    "
                    class="flex items-center justify-center h-[310px]"
                  >
                    <p class="text-center">
                      No results found for
                      <span class="font-medium">{{ searchQuery }}</span>
                    </p>
                  </div>
                  <div
                    v-if="isDiscoveryKeywordLoading"
                    class="flex items-center justify-center h-[310px]"
                  >
                    <span class="w-[30px] h-[30px]">
                      <LoaderCircle
                        :size="30"
                        :stroke-width="1.75"
                        class="loader"
                      />
                    </span>
                  </div>
                  <div
                    class="flex items-center justify-between gap-2 border-t px-3 py-2 min-h-[57px]"
                    :class="
                      mode === 'dark'
                        ? 'border-dark-50 bg-dark-70'
                        : 'bg-white-100'
                    "
                  >
                    <div
                      class="text-[15px]"
                      :class="[
                        mode === 'dark' ? 'text-dark-15' : 'text-gray-600',
                      ]"
                    >
                      Showing
                      {{ discoveryKeywordSearchResults.results.length }} of
                      {{ discoveryKeywordSearchResults.count }}
                    </div>
                    <div class="flex items-center gap-2">
                      <button
                        :class="[
                          !discoveryKeywordSearchResults.previous
                            ? 'opacity-50 pointer-events-none'
                            : 'opacity-100',
                        ]"
                        @click="previousPage('keywordplanner')"
                        class="button small-button primary-button"
                      >
                        <ArrowLeft
                          :size="20"
                          :stroke-width="1.75"
                          class="mt-[2px]"
                        />
                        Previous
                      </button>
                      <button
                        :class="[
                          !discoveryKeywordSearchResults.next
                            ? 'opacity-50 pointer-events-none'
                            : 'opacity-100',
                        ]"
                        @click="nextPage('keywordplanner')"
                        class="button small-button primary-button"
                      >
                        Next
                        <ArrowRight
                          :size="20"
                          :stroke-width="1.75"
                          class="mt-[2px]"
                        />
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  class="border rounded-md overflow-hidden"
                  :class="
                    mode === 'dark'
                      ? 'border-dark-50 bg-dark-70'
                      : 'bg-white-100'
                  "
                >
                  <div
                    class="p-3 border-b"
                    :class="[mode === 'dark' ? 'border-dark-50' : '']"
                  >
                    <h3 class="leading-none mb-2 font-semibold tex-[17px]">
                      Keyword spy
                    </h3>
                    <p
                      class="text-[15px] leading-none"
                      :class="
                        mode === 'dark' ? 'text-dark-15' : 'text-gray-600'
                      "
                    >
                      Total {{ spyKeywordSearchResults.count }}
                      {{
                        spyKeywordSearchResults.count > 1 ? "results" : "result"
                      }}
                      found
                    </p>
                  </div>
                  <div v-if="spyKeywordSearchResults.count > 0">
                    <div
                      class="p-3 h-[310px] overflow-y-auto without-scrollbar"
                    >
                      <ul class="grid gap-1 pl-4 list-disc">
                        <li
                          :class="[
                            mode === 'dark' ? 'text-dark-15' : 'text-gray-600',
                          ]"
                          v-for="(
                            result, key
                          ) in spyKeywordSearchResults.results"
                          :key="key"
                        >
                          <div
                            @click="redirectTo(result.identifier, 'search')"
                            class="text-[15px] hover:text-primary-100 cursor-pointer"
                            :class="[
                              mode === 'dark'
                                ? 'text-dark-15'
                                : 'text-gray-600',
                            ]"
                          >
                            {{ result.content }}
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div
                    v-if="
                      spyKeywordSearchResults.count === 0 &&
                      !isSpyKeywordLoading
                    "
                    class="flex items-center justify-center h-[310px]"
                  >
                    <p class="text-center">
                      No results found for
                      <span class="font-medium">{{ searchQuery }}</span>
                    </p>
                  </div>
                  <div
                    v-if="isSpyKeywordLoading"
                    class="flex items-center justify-center h-[310px]"
                  >
                    <span class="w-[30px] h-[30px]">
                      <LoaderCircle
                        :size="30"
                        :stroke-width="1.75"
                        class="loader"
                      />
                    </span>
                  </div>
                  <div
                    class="flex items-center justify-between gap-2 border-t px-3 py-2 min-h-[57px]"
                    :class="
                      mode === 'dark'
                        ? 'border-dark-50 bg-dark-70'
                        : 'bg-white-100'
                    "
                  >
                    <div
                      class="text-[15px]"
                      :class="[
                        mode === 'dark' ? 'text-dark-15' : 'text-gray-600',
                      ]"
                    >
                      Showing {{ spyKeywordSearchResults.results.length }} of
                      {{ spyKeywordSearchResults.count }}
                    </div>
                    <div class="flex items-center gap-2">
                      <button
                        :class="[
                          !spyKeywordSearchResults.previous
                            ? 'opacity-50 pointer-events-none'
                            : 'opacity-100',
                        ]"
                        @click="previousPage('search')"
                        class="button small-button primary-button gap-2"
                      >
                        <ArrowLeft
                          :size="20"
                          :stroke-width="1.75"
                          class="mt-[2px]"
                        />
                        Previous
                      </button>
                      <button
                        :class="[
                          !spyKeywordSearchResults.next
                            ? 'opacity-50 pointer-events-none'
                            : 'opacity-100',
                        ]"
                        @click="nextPage('search')"
                        class="button small-button primary-button gap-2"
                      >
                        Next
                        <ArrowRight
                          :size="20"
                          :stroke-width="1.75"
                          class="mt-[2px]"
                        />
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  class="border rounded-md overflow-hidden"
                  :class="
                    mode === 'dark'
                      ? 'border-dark-50 bg-dark-70'
                      : 'bg-white-100'
                  "
                >
                  <div
                    class="p-3 border-b"
                    :class="[mode === 'dark' ? 'border-dark-50' : '']"
                  >
                    <h3 class="leading-none mb-2 font-semibold tex-[17px]">
                      Keyword ideas
                    </h3>
                    <p
                      class="text-[15px] leading-none"
                      :class="
                        mode === 'dark' ? 'text-dark-15' : 'text-gray-600'
                      "
                    >
                      Total {{ keywordIdeasSearchResults.count }}
                      {{
                        keywordIdeasSearchResults.count > 1
                          ? "results"
                          : "result"
                      }}
                      found
                    </p>
                  </div>
                  <div v-if="keywordIdeasSearchResults.count > 0">
                    <div
                      class="p-3 h-[310px] overflow-y-auto without-scrollbar"
                    >
                      <ul class="grid gap-1 pl-4 list-disc">
                        <li
                          v-for="(
                            result, key
                          ) in keywordIdeasSearchResults.results"
                          :key="key"
                          :class="[
                            mode === 'dark' ? 'text-dark-15' : 'text-gray-600',
                          ]"
                        >
                          <div
                            @click="redirectToKeywordIdea(result)"
                            class="text-[15px] hover:text-primary-100 cursor-pointer"
                            :class="[
                              mode === 'dark'
                                ? 'text-dark-15'
                                : 'text-gray-600',
                            ]"
                          >
                            {{ result.content }}
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div
                    v-if="
                      keywordIdeasSearchResults.count === 0 &&
                      !isKeywordIdeasLoading
                    "
                    class="flex items-center justify-center h-[310px]"
                  >
                    <p class="text-center">
                      No results found for
                      <span class="font-medium">{{ searchQuery }}</span>
                    </p>
                  </div>
                  <div
                    v-if="isKeywordIdeasLoading"
                    class="flex items-center justify-center h-[310px]"
                  >
                    <span class="w-[30px] h-[30px]">
                      <LoaderCircle
                        :size="30"
                        :stroke-width="1.75"
                        class="loader"
                      />
                    </span>
                  </div>
                  <div
                    class="flex items-center justify-between gap-2 border-t px-3 py-2 min-h-[57px]"
                    :class="
                      mode === 'dark'
                        ? 'border-dark-50 bg-dark-70'
                        : 'bg-white-100'
                    "
                  >
                    <div
                      class="text-[15px]"
                      :class="[
                        mode === 'dark' ? 'text-dark-15' : 'text-gray-600',
                      ]"
                    >
                      Showing
                      {{ keywordIdeasSearchResults.results.length }} of
                      {{ keywordIdeasSearchResults.count }}
                    </div>
                    <div class="flex items-center gap-2">
                      <button
                        :class="[
                          !keywordIdeasSearchResults.previous
                            ? 'opacity-50 pointer-events-none'
                            : 'opacity-100',
                        ]"
                        @click="previousPage('keywordideas')"
                        class="button small-button primary-button gap-2"
                      >
                        <ArrowLeft
                          :size="20"
                          :stroke-width="1.75"
                          class="mt-[2px]"
                        />
                        Previous
                      </button>
                      <button
                        :class="[
                          !keywordIdeasSearchResults.next
                            ? 'opacity-50 pointer-events-none'
                            : 'opacity-100',
                        ]"
                        @click="nextPage('keywordideas')"
                        class="button small-button primary-button gap-2"
                      >
                        Next
                        <ArrowRight
                          :size="20"
                          :stroke-width="1.75"
                          class="mt-[2px]"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useUserStore } from "../stores/user";
import ApiRequest from "../libs/ApiRequest";
import helper from "../libs/helper";
import {
  Search,
  LoaderCircle,
  X,
  ArrowRight,
  ArrowLeft,
} from "lucide-vue-next";

export default {
  setup() {
    const userStore = useUserStore();
    return {
      userStore: userStore,
    };
  },
  props: {
    mode: String,
  },
  components: {
    Search,
    LoaderCircle,
    X,
    ArrowRight,
    ArrowLeft,
  },
  data() {
    return {
      isSearch: false,
      isSpyKeywordLoading: false,
      isDiscoveryKeywordLoading: false,
      isKeywordIdeasLoading: false,
      activeTab: "keyword-discovery",
      searchQuery: "",
      timeout: null,
      spyKeywordSearchResults: {
        results: [],
        next: null,
        previous: null,
        count: 0,
      },
      discoveryKeywordSearchResults: {
        results: [],
        next: null,
        previous: null,
        count: 0,
      },
      keywordIdeasSearchResults: {
        results: [],
        next: null,
        previous: null,
        count: 0,
      },
      currentKWSPage: 0,
      currentKWDPage: 0,
      currentKWIPage: 0,
    };
  },
  methods: {
    getSearchResults() {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
      this.timeout = setTimeout(() => {
        this.seachForKeywords("search");
        this.seachForKeywords("keywordplanner");
        this.seachForKeywords("keywordideas");
      }, 500);
    },
    async seachForKeywords(source) {
      if (source === "search") {
        this.isSpyKeywordLoading = true;
        this.spyKeywordSearchResults = {
          results: [],
          next: null,
          previous: null,
          count: 0,
        };
      }
      if (source === "keywordplanner") {
        this.isDiscoveryKeywordLoading = true;
        this.discoveryKeywordSearchResults = {
          results: [],
          next: null,
          previous: null,
          count: 0,
        };
      }
      if (source === "keywordideas") {
        this.isKeywordIdeasLoading = true;
        this.keywordIdeasSearchResults = {
          results: [],
          next: null,
          previous: null,
          count: 0,
        };
      }
      try {
        let url = `api/advanced_search/search?q=${this.searchQuery}&source=${source}&page=${this.currentKWSPage}`;
        if (source === "keywordplanner") {
          url = `api/advanced_search/search?q=${this.searchQuery}&source=${source}&page=${this.currentKWDPage}`;
        }
        if (source === "keywordideas") {
          url = `api/advanced_search/search?q=${this.searchQuery}&source=${source}&page=${this.currentKWIPage}`;
        }
        if (this.currentKWSPage !== 0) {
          url = `api/advanced_search/search?q=${
            this.searchQuery
          }&limit=${50}&offset=${
            50 * this.currentKWSPage
          }&source=${source}&page=${this.currentKWSPage}`;
        }
        if (this.currentKWDPage !== 0) {
          url = `api/advanced_search/search?q=${
            this.searchQuery
          }&limit=${50}&offset=${
            50 * this.currentKWDPage
          }&source=${source}&page=${this.currentKWDPage}`;
        }
        if (this.currentKWIPage !== 0) {
          url = `api/advanced_search/search?q=${
            this.searchQuery
          }&limit=${50}&offset=${
            50 * this.currentKWIPage
          }&source=${source}&page=${this.currentKWIPage}`;
        }
        const { data } = await ApiRequest().get(url);
        if (source === "search") {
          this.spyKeywordSearchResults = data;
        }
        if (source === "keywordplanner") {
          this.discoveryKeywordSearchResults = data;
        }
        if (source === "keywordideas") {
          this.keywordIdeasSearchResults = data;
        }
      } catch (error) {
        const extractedError = helper.extractAlert(error);
        if (source === "search") {
          this.spyKeywordSearchResults = {
            results: [],
            next: null,
            previous: null,
            count: 0,
          };
          this.isSpyKeywordLoading = false;
          this.currentKWSPage = 0;
        }
        if (source === "keywordplanner") {
          this.discoveryKeywordSearchResults = {
            results: [],
            next: null,
            previous: null,
            count: 0,
          };
          this.isDiscoveryKeywordLoading = false;
          this.currentKWDPage = 0;
        }
        if (source === "keywordideas") {
          this.keywordIdeasSearchResults = {
            results: [],
            next: null,
            previous: null,
            count: 0,
          };
          this.isKeywordIdeasLoading = false;
          this.currentKWIPage = 0;
        }
        if (extractedError.redirect) {
          this.userStore.signOut();
          this.$router.push({ name: extractedError.redirect });
        }
      } finally {
        this.isSpyKeywordLoading = false;
        this.isDiscoveryKeywordLoading = false;
        this.isKeywordIdeasLoading = false;
      }
    },
    previousPage(source) {
      if (source === "search") {
        this.currentKWSPage -= 1;
      }
      if (source === "keywordplanner") {
        this.currentKWDPage -= 1;
      }
      if (source === "keywordideas") {
        this.currentKWIPage -= 1;
      }
      this.seachForKeywords(source);
    },
    nextPage(source) {
      if (source === "search") {
        this.currentKWSPage += 1;
      }
      if (source === "keywordplanner") {
        this.currentKWDPage += 1;
      }
      if (source === "keywordideas") {
        this.currentKWIPage += 1;
      }
      this.seachForKeywords(source);
    },
    closeSearch() {
      this.searchQuery = "";
      this.isSearch = false;
      this.isSpyKeywordLoading = false;
      this.isDiscoveryKeywordLoading = false;
      this.isKeywordIdeasLoading = false;
      this.spyKeywordSearchResults = {
        results: [],
        next: null,
        previous: null,
        count: 0,
      };
      this.discoveryKeywordSearchResults = {
        results: [],
        next: null,
        previous: null,
        count: 0,
      };
      this.keywordIdeasSearchResults = {
        results: [],
        next: null,
        previous: null,
        count: 0,
      };
    },
    showSearch() {
      this.isSearch = true;
      setTimeout(() => {
        document.getElementById("search").focus();
      }, 100);
    },
    redirectTo(identifier, type) {
      this.closeSearch();
      if (type === "search") {
        this.$router.push({
          name: "serp-report-ta",
          params: { uuid: identifier },
        });
      }
      if (type === "keywordplanner") {
        this.$router.push({
          name: "KeywordPlannerDetail",
          params: { uuid: identifier },
        });
      }
    },
    redirectToKeywordIdea(keyword) {
      this.closeSearch();
      this.$router
        .push({
          name: "KeywordPlannerDetail",
          params: { uuid: keyword.identifier },
          query: { keyword: keyword.content },
        })
        .then(() => {
          window.location.reload();
        });
    },
  },
};
</script>
